
import { useNavigate } from 'react-router-dom';
import type { Article } from '../../../mocks/blog';

interface BlogCardProps {
  article: Article;
  index: number;
}

export default function BlogCard({ article, index }: BlogCardProps) {
  const navigate = useNavigate();

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div
      onClick={() => navigate(`/blog/${article.id}`)}
      className="group relative bg-white/[0.03] border border-amber-500/10 rounded-sm overflow-hidden cursor-pointer hover:border-amber-400/40 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-1"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Cover image */}
      <div className="relative w-full h-52 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {/* Tags overlay */}
        <div className="absolute bottom-3 left-4 flex flex-wrap gap-2">
          {article.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-0.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs tracking-wider rounded-sm backdrop-blur-sm"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-px bg-amber-500/50" />
          <span
            className="text-amber-500/60 text-xs tracking-[0.25em]"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            {formatDate(article.date)}
          </span>
        </div>

        <h3
          className="text-white/90 text-lg font-semibold leading-snug mb-3 group-hover:text-amber-400 transition-colors duration-300 line-clamp-2"
          style={{ fontFamily: "'Noto Serif TC', serif" }}
        >
          {article.title}
        </h3>

        <p
          className="text-white/40 text-sm leading-relaxed line-clamp-3 mb-5"
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          {article.summary}
        </p>

        <div className="flex items-center gap-2 text-amber-400/70 group-hover:text-amber-400 transition-colors duration-300">
          <span
            className="text-xs tracking-[0.2em]"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            閱讀全文
          </span>
          <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>

      {/* Hover glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  );
}
